import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { billingService, BillingSettingsUpdate, PointOfSaleCreate, PointOfSaleUpdate, InvoiceFromSaleRequest } from '@/services/billing';

export const useBillingSettings = () =>
  useQuery({
    queryKey: ['billing-settings'],
    queryFn: () => billingService.getSettings(),
  });

export const useUpdateBillingSettings = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: BillingSettingsUpdate) => billingService.updateSettings(payload),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['billing-settings'] }),
  });
};

export const useUploadCertificate = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (file: File) => billingService.uploadCertificate(file),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['billing-settings'] }),
  });
};

export const useUploadPrivateKey = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (file: File) => billingService.uploadPrivateKey(file),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['billing-settings'] }),
  });
};

export const useTestConnection = () =>
  useMutation({
    mutationFn: () => billingService.testConnection(),
  });

export const usePointsOfSale = () =>
  useQuery({
    queryKey: ['points-of-sale'],
    queryFn: () => billingService.listPointsOfSale(),
  });

export const useCreatePointOfSale = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: PointOfSaleCreate) => billingService.createPointOfSale(payload),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['points-of-sale'] }),
  });
};

export const useUpdatePointOfSale = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id: number; payload: PointOfSaleUpdate }) =>
      billingService.updatePointOfSale(id, payload),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['points-of-sale'] }),
  });
};

export const useDeletePointOfSale = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => billingService.deletePointOfSale(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['points-of-sale'] }),
  });
};

// === Comprobantes ===

export const useInvoicesForSale = (saleId?: number) =>
  useQuery({
    queryKey: ['invoices', saleId],
    queryFn: () => billingService.listInvoices({ saleId }),
    enabled: !!saleId,
  });

// Comprobantes de un mes y punto de venta: el detalle detras del resumen de facturacion.
export const useInvoicesOfMonth = (
  year: number,
  month: number,
  pointOfSaleId?: number,
  enabled = true,
) =>
  useQuery({
    queryKey: ['invoices', 'month', year, month, pointOfSaleId ?? null],
    queryFn: () =>
      billingService.listInvoices({ year, month, pointOfSaleId, onlyApproved: true }),
    enabled: enabled && !!pointOfSaleId,
  });

export const useCreateInvoiceFromSale = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ saleId, payload }: { saleId: number; payload: InvoiceFromSaleRequest }) =>
      billingService.createInvoiceFromSale(saleId, payload),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['invoices'] }),
  });
};

export const useCreateCreditNote = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ invoiceId, motivo }: { invoiceId: number; motivo?: string }) =>
      billingService.createCreditNote(invoiceId, { motivo }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['invoices'] }),
  });
};

export const useBillingSummary = (year: number, month?: number, enabled = true) =>
  useQuery({
    queryKey: ['billing-summary', year, month ?? null],
    queryFn: () => billingService.getSummary(year, month),
    enabled,
  });
